import { useEffect, useRef, useState } from 'react'
import { useLocation } from '@tanstack/react-router'
import { ChevronDown, ArrowRight, Menu, X } from 'lucide-react'
import { useHeroConfig, bgById } from '@/lib/heroConfig'

// Flip to solid after a small scroll, not once the whole hero has passed —
// reads as an immediate response to scrolling rather than a delayed one.
const SCROLL_SOLID_THRESHOLD_PX = 24

/**
 * Header for the umbrella platform brand (apex scamdefense.org).
 * Distinct from the consumer SiteHeader used on the NASAA build:
 * blue identity, B2B nav (Solutions / Platform / How It Works / About),
 * and a single primary CTA.
 */

type MenuItem = { label: string; href: string; external?: boolean; note?: string }

const SOLUTIONS: MenuItem[] = [
  { label: 'For State Agencies', href: '/solutions/state-agencies', note: 'State-specific education & reporting' },
  { label: 'For Banks & Credit Unions', href: '/solutions/financial-institutions', note: 'Protect account holders from fraud' },
  { label: 'For Wealth Advisors & RIAs', href: '/solutions/wealth-advisors', note: 'Client and family protection, your brand' },
  { label: 'For Community Partners', href: '/solutions/community-partners', note: 'Ready-to-share materials' },
]

export default function UmbrellaHeader() {
  const [open, setOpen] = useState<'solutions' | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const ref = useRef<HTMLElement>(null)
  const location = useLocation()
  // Apex homepage reads the on-page hero configurator (layout + background +
  // scroll theme). Ignored on every other route.
  const cfg = useHeroConfig()

  // Two header treatments float over a hero and go solid on scroll:
  //  - /home-v2 and /home-v3 (design-review): dark navy hero, header goes navy.
  //  - / (apex homepage): the configurable hero panel; header is transparent
  //    at the top and turns into a light/white theme once it sticks.
  // Every other page keeps the standard solid-white header.
  const DARK_HERO_ROUTES = ['/home-v2', '/home-v3']
  const isDarkHeroRoute = DARK_HERO_ROUTES.includes(location.pathname)
  const isApexHome = location.pathname === '/'
  const overlayHero = isApexHome || isDarkHeroRoute
  const [scrolled, setScrolled] = useState(!overlayHero)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(null)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!overlayHero) {
      setScrolled(true)
      return
    }
    const onScroll = () => setScrolled(window.scrollY > SCROLL_SOLID_THRESHOLD_PX)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [overlayHero])

  // The mobile menu drops an opaque panel below the header, so force the
  // compact/solid treatment while it's open even at the very top of the page.
  const compact = scrolled || mobileOpen

  // Derive the header treatment. `light` = white text/logo.
  //  - Non-overlay pages: solid white bar, dark text.
  //  - Dark-hero review routes: transparent -> navy, always light text.
  //  - Apex home: driven by the configurator. Inset layout = a light/white bar
  //    above the rounded hero. Full-bleed = transparent at the top with text
  //    that contrasts the chosen background, then white or navy on scroll.
  let light: boolean
  let bgClass: string

  if (!overlayHero) {
    light = false
    bgClass = 'border-slate-200/70 bg-white/85 backdrop-blur-md'
  } else if (isDarkHeroRoute) {
    light = true
    bgClass = compact
      ? 'border-white/10 bg-[#0f1a3d]/95 shadow-sm backdrop-blur-md'
      : 'border-transparent bg-transparent'
  } else if (cfg.layout === 'inset') {
    light = false
    bgClass = compact
      ? 'border-slate-200/70 bg-white/90 shadow-sm backdrop-blur-md'
      : 'border-transparent bg-white/85 backdrop-blur-md'
  } else if (compact) {
    // Full-bleed, stuck: white or navy per the "Header on scroll" toggle.
    light = cfg.headerScroll === 'navy'
    bgClass =
      cfg.headerScroll === 'navy'
        ? 'border-white/10 bg-[#0f1a3d]/95 shadow-sm backdrop-blur-md'
        : 'border-slate-200/70 bg-white/90 shadow-sm backdrop-blur-md'
  } else {
    // Full-bleed, at the top: transparent, text contrasts the hero background.
    light = bgById(cfg.bgId).theme === 'dark'
    bgClass = 'border-transparent bg-transparent'
  }

  // Over-hero headers float with no reserved layout space so the hero runs up
  // under them; every other page keeps the normal in-flow sticky header.
  const positionClass = overlayHero ? 'fixed inset-x-0' : 'sticky'

  return (
    <header
      ref={ref}
      className={`${positionClass} top-0 z-40 border-b transition-colors duration-300 ${bgClass}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
        <a href="/" className="flex shrink-0 items-center gap-2.5">
          <img
            src="/scamdefense-logo.png"
            alt="ScamDefense"
            className="h-9 w-9"
            width={36}
            height={36}
          />
          <span
            className={`text-xl font-semibold tracking-tight transition-colors duration-300 ${light ? 'text-white' : 'text-slate-900'}`}
          >
            ScamDefense
            <span className={`font-medium transition-colors duration-300 ${light ? 'text-blue-200/70' : 'text-slate-400'}`}>
              .org
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex">
          <DropdownNav
            label="Solutions"
            items={SOLUTIONS}
            isOpen={open === 'solutions'}
            onToggle={() => setOpen(open === 'solutions' ? null : 'solutions')}
            onPick={() => setOpen(null)}
            light={light}
          />
          <NavLink href="/platform" light={light}>Platform</NavLink>
          <NavLink href="/how-it-works" light={light}>How It Works</NavLink>
          <NavLink href="/about" light={light}>About</NavLink>
        </nav>

        <div className="hidden shrink-0 items-center gap-6 lg:flex">
          {/* Portal has no custom domain yet; swap for partners.scamdefense.org
              once the domain is attached to scamdefense-sponsors. */}
          <a
            href="https://scamdefense-sponsors.vercel.app"
            className={`text-base font-medium transition-colors duration-300 ${
              light ? 'text-white/90 hover:text-white' : 'text-slate-800 hover:text-slate-950'
            }`}
          >
            Sign in
          </a>
          <a
            href="/contact"
            className={`inline-flex items-center gap-1.5 text-base font-medium transition-colors duration-300 ${
              light ? 'text-white/90 hover:text-white' : 'text-slate-800 hover:text-slate-950'
            }`}
          >
            Talk to us
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen(v => !v)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg transition-colors duration-300 lg:hidden ${
            light ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-slate-200/70 bg-white lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            <p className="px-1 pt-2 text-xs font-semibold uppercase text-slate-400">Solutions</p>
            {SOLUTIONS.map(item => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-slate-700 transition hover:bg-blue-50/70"
              >
                {item.label}
              </a>
            ))}
            <div className="my-2 border-t border-slate-100" />
            {[
              { label: 'Platform', href: '/platform' },
              { label: 'How It Works', href: '/how-it-works' },
              { label: 'About', href: '/about' },
            ].map(item => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-slate-700 transition hover:bg-blue-50/70"
              >
                {item.label}
              </a>
            ))}
            <a
              href="https://scamdefense-sponsors.vercel.app"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-slate-800 transition hover:bg-blue-50/70"
            >
              Sign in
            </a>
            <a
              href="/contact"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-[15px] font-medium text-slate-800 transition hover:bg-blue-50/70"
            >
              Talk to us
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}

function NavLink({
  href,
  children,
  light,
}: {
  href: string
  children: React.ReactNode
  light: boolean
}) {
  return (
    <a
      href={href}
      className={`cursor-pointer rounded-md px-3 py-2 text-base font-medium transition-colors duration-300 ${
        light ? 'text-blue-100 hover:text-white' : 'text-slate-700 hover:text-slate-900'
      }`}
    >
      {children}
    </a>
  )
}

function DropdownNav({
  label,
  items,
  isOpen,
  onToggle,
  onPick,
  light,
}: {
  label: string
  items: MenuItem[]
  isOpen: boolean
  onToggle: () => void
  onPick: () => void
  light: boolean
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-2 text-base font-medium transition-colors duration-300 ${
          light ? 'text-blue-100 hover:text-white' : 'text-slate-700 hover:text-slate-900'
        }`}
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute start-0 z-50 mt-2 w-72 rounded-2xl bg-white p-2 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)] ring-1 ring-slate-200/70">
          <ul>
            {items.map(item => (
              <li key={item.label}>
                <a
                  href={item.href}
                  {...(item.external ? { target: '_blank', rel: 'noreferrer' } : {})}
                  onClick={onPick}
                  className="block rounded-xl px-3 py-2.5 transition hover:bg-blue-50/70"
                >
                  <span className="block text-[15px] font-medium text-slate-800">{item.label}</span>
                  {item.note && (
                    <span className="mt-0.5 block text-[13px] text-slate-500">{item.note}</span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
